import React, { Fragment, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { kitchenAreaApi } from '@app/store';
import { IKitchenArea, KitchenAreaDTO } from '@app/common';
import { Button, CardButtonActionMode, Form, Panel } from '@app/components';
import { KitchenAreaForm } from './KitchenAreaForm';
import { KitchenAreaFormValues, transformToUpdatableFormKitchenArea } from './kitchen-area-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { KitchenAreaCard } from './KitchenAreaCard';
import { CheckPermissions } from '@app/components/CheckPermissions';

interface KitchenAreaUpdateProps {
  kitchenArea?: Partial<IKitchenArea>;
  isFetching: boolean;
  toggle: (mode: CardButtonActionMode) => void;
  onDelete: (kitchenArea: Partial<IKitchenArea>) => void;
}

export const KitchenAreaUpdate = ({ kitchenArea, isFetching, toggle, onDelete }: KitchenAreaUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const [update, { isLoading, error: apiErrors }] = kitchenAreaApi.useUpdateOneKitchenAreasMutation();

  const formMethods = useForm<KitchenAreaFormValues>({
    resolver: classValidatorResolver(KitchenAreaDTO),
    defaultValues: transformToUpdatableFormKitchenArea(kitchenArea)
  });

  const onUpdate = async (formValues: Partial<IKitchenArea>) => {
    try {
      const updatedKitchenArea = await update({ _id: kitchenArea?._id!, ...formValues }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedKitchenArea });

      toast.success('Votre lieu de fabrication a bien été modifié.')
    } catch {
      toast.error('Une erreur est survenue lors de la modification de votre lieu de fabrication.')
    }
  }

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormKitchenArea(kitchenArea));
  }, [kitchenArea, formMethods.reset]);

  return (
    <Fragment>
      <KitchenAreaCard
        kitchenArea={kitchenArea}
        formMethods={formMethods}
        toggle={toggle} />

      <Panel.Content>

        <Form formMethods={formMethods} apiErrors={apiErrors} isLoading={isFetching}>

          <Panel.FormContainer>

            <CheckPermissions permissions={['dashboard::kitchen-areas.delete']}>
              <Panel.Header>
                <Button
                  title="Supprimer"
                  disabled={!kitchenArea}
                  bordered
                  important
                  onPress={() => onDelete(kitchenArea!)} />
              </Panel.Header>
            </CheckPermissions>

            <KitchenAreaForm />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              disabled={isFetching}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>
        </Form>
      </Panel.Content>
    </Fragment>
  );
};
